/**
 * Maps Vivideo's own planner output (V2VideoPlan, ported from LongVid) onto the
 * existing V2PlanCard `Plan` shape, so the SAME plan card renders our plans with
 * zero card changes. The user's chosen avatar/voice (from the home chatbox) ride
 * along as id + name + image so the card shows them immediately — no HeyGen
 * session needed at plan time. When nothing is chosen the card shows "Auto-selected".
 */
import type { Plan } from '@/features/plan/PlanCard';
import type { V2VideoPlan } from '@/api/v2Service';

/** Plan + the chosen cast/brand display fields (the card prefers these over the
 *  session-scoped resource lookup, which has no session yet at plan time). */
export type V2CardPlan = Plan & {
  avatar_name?: string;
  avatar_image?: string;
  voice_name?: string;
  voice_preview_url?: string; // stock sample of the chosen voice (so the card can audition it)
  brand_ref?: string;
  brand_name?: string;
  brand_logo?: string;
};

export interface CardPlanSelection {
  avatarId?: string | null; avatarName?: string | null; avatarImage?: string | null;
  voiceId?: string | null; voiceName?: string | null; voicePreviewUrl?: string | null;
  brandRef?: string | null; brandName?: string | null; brandLogo?: string | null;
}

// The planner stamps "Auto-selected" when the user left the cast on auto — treat
// that as "no explicit name" so the card's own fallback ("Auto-selected") shows.
const realName = (n?: string | null) => (n && n !== 'Auto-selected' ? n : undefined);

// The planner (Claude) occasionally emits `scenes` as a JSON-ENCODED STRING instead
// of a real array, despite the tool schema. That value gets stored verbatim on the
// order, so `scenes.map(...)` below used to throw ("scenes.map is not a function")
// and crash the whole project page into the ErrorBoundary. Normalize to an array:
// parse a stringified list, and treat anything non-array as empty.
const asSceneArray = (raw: unknown): NonNullable<V2VideoPlan['scenes']> => {
  if (Array.isArray(raw)) return raw as NonNullable<V2VideoPlan['scenes']>;
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) return parsed as NonNullable<V2VideoPlan['scenes']>;
    } catch { /* not valid JSON — fall through to empty */ }
  }
  return [];
};

export function videoPlanToCardPlan(vp: V2VideoPlan, sel?: CardPlanSelection): V2CardPlan {
  const s = vp.settings || ({} as V2VideoPlan['settings']);
  return {
    title: vp.title,
    concept_explanation: vp.script,
    style_explanation: vp.style,
    duration_seconds: Math.round(Number(s.duration_sec) || 0),
    orientation: s.orientation,
    language: s.language,
    cc_enabled: s.captions_enabled,
    // The chosen cast (id forwarded to HeyGen at Generate; name + image shown on
    // the card). Falls back to the planner's suggested name. Undefined → "Auto-selected".
    avatar_id: sel?.avatarId || undefined,
    avatar_name: realName(sel?.avatarName) || realName(vp.avatar?.name),
    avatar_image: sel?.avatarImage || undefined,
    voice_id: sel?.voiceId || undefined,
    voice_name: realName(sel?.voiceName) || realName(vp.voice?.name),
    voice_preview_url: sel?.voicePreviewUrl || undefined,
    brand_ref: sel?.brandRef || undefined,
    brand_name: sel?.brandName || undefined,
    brand_logo: sel?.brandLogo || undefined,
    scenes: asSceneArray(vp.scenes).map((sc) => ({
      title: sc.name,
      start: Math.round(Number(sc.start_sec) || 0),
      end: Math.round(Number(sc.end_sec) || 0),
      type: String(sc.track || '').trim().toLowerCase().startsWith('a') ? 'a_roll' : 'b_roll',
      vo_script: sc.voiceover,
      visual_explanation: sc.visual,
      ...(sc.speech ? { speech: sc.speech } : {}),
    })),
    ...(Array.isArray(vp.files) ? { files: vp.files } : {}),
    ...(Number(vp.requested_duration_sec) > 0 ? { requested_duration_sec: Number(vp.requested_duration_sec) } : {}),
  };
}
